// src/pages/GenerateCOA.js
// Wizard redesigned based on Tecmol Farmacéutica real analysis request form (FOR-DFQ-070)
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { saveCertificate, generateCertificateId, uploadPaymentScreenshot, uploadProductLabel } from '../firebase/services';
import { getUserLogo } from './Settings';
import { CERT_TYPES, CRYPTO_WALLETS } from '../config/site';

const STEPS = ['type', 'contact', 'sample', 'analysis', 'payment', 'review'];

export default function GenerateCOA() {
  const { user }    = useAuth();
  const { lang, t } = useLang();
  const navigate    = useNavigate();

  const [step, setStep] = useState(0);

  // Step 0 — Certificate type
  const [certType, setCertType] = useState(CERT_TYPES[0]);

  // Step 1 — Contact info
  // Pre-fill from Settings
  const [contact, setContact] = useState({
    companyName: localStorage.getItem('coalab_company_name') || '',
    address:     localStorage.getItem('coalab_company_addr') || '',
    requestDate: new Date().toISOString().slice(0,10),
  });

  // Step 2 — Sample info
  const [sample, setSample] = useState({
    productName: '', lot: '', presentation: '', sampleQty: '',
    activeIngredient: '', doseConcentration: '',
    mfgDate: '', expDate: '',
    supplier: '', manufacturer: localStorage.getItem('coalab_def_country') || '',
    sanitaryRegistry: '', samplingDate: '',
    line: '',
    category: '',
    pharmaceuticalForm: '',
    sampleType: [],
    hasManufacturerCert: '',
    storageCondition: '',
    transportTemp: '',
    methodology: localStorage.getItem('coalab_def_method')
      ? [localStorage.getItem('coalab_def_method')]
      : [],
    methodValidation: '',
  });

  // Step 3 — Analysis requested (only fields client fills in)
  const [analysis, setAnalysis] = useState({
    // Physicochemical — each field has { selected: bool, spec: string }
    aspecto: { selected: false, spec: '' },
    ph: { selected: false, spec: '' },
    conductividad: { selected: false, spec: '' },
    toc: { selected: false, spec: '' },
    nitritos: { selected: false, spec: '' },
    nitratos: { selected: false, spec: '' },
    color: { selected: false, spec: '' },
    turbiedad: { selected: false, spec: '' },
    cloroLibre: { selected: false, spec: '' },
    durezaTotal: { selected: false, spec: '' },
    sulfatos: { selected: false, spec: '' },
    alcalinidad: { selected: false, spec: '' },
    metalesPesados: { selected: false, spec: '' },
    densidad: { selected: false, spec: '' },
    kFisher: { selected: false, spec: '' },
    solubilidad: { selected: false, spec: '' },
    puntoDeFusion: { selected: false, spec: '' },
    viscosidad: { selected: false, spec: '' },
    perdidaPorSecado: { selected: false, spec: '' },
    indiceRefraccion: { selected: false, spec: '' },
    residuoIgnicion: { selected: false, spec: '' },
    rotacionOptica: { selected: false, spec: '' },
    indiceYodo: { selected: false, spec: '' },
    indiceSaponificacion: { selected: false, spec: '' },
    acidez: { selected: false, spec: '' },
    materiaAnionica: { selected: false, spec: '' },
    friabilidad: { selected: false, spec: '' },
    dureza: { selected: false, spec: '' },
    desintegracion: { selected: false, spec: '' },
    disolucion: { selected: false, spec: '' },
    pesoPromedio: { selected: false, spec: '' },
    volumetria: { selected: false, spec: '' },
    valoracionUV: { selected: false, spec: '' },
    valoracionHPLC: { selected: false, spec: '' },
    otros: { selected: false, spec: '' },
    observations: '',
  });

  // Step 4 — Payment
  const [selectedCrypto, setSelectedCrypto] = useState(CRYPTO_WALLETS[0]);
  const [txHash, setTxHash]         = useState('');
  const [screenshot, setScreenshot] = useState('');
  const screenshotRef = useRef();
  const [labelImage, setLabelImage] = useState('');
  const labelRef = useRef();

  // Step 5 — Review options
  const [includeLogo, setIncludeLogo] = useState(true);
  const includeQR = true;

  const [certId]    = useState(generateCertificateId());
  const [saving, setSaving]     = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]       = useState('');

  const setC = (k) => (e) => setContact(p => ({ ...p, [k]: e.target.value }));
  const setS = (k) => (e) => setSample(p => ({ ...p, [k]: e.target.value }));

  const toggleMulti = (field, val) => setSample(p => ({
    ...p,
    [field]: p[field].includes(val) ? p[field].filter(v => v !== val) : [...p[field], val],
  }));

  const toggleAnalysis = (key) => setAnalysis(p => ({ ...p, [key]: { ...p[key], selected: !p[key].selected } }));
  const setAnalysisSpec = (key, val) => setAnalysis(p => {
    if (key === 'observations') return { ...p, observations: val };
    return { ...p, [key]: { ...p[key], spec: val } };
  });

  const handleNext = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setStep(s => Math.max(s - 1, 0));

  const handleScreenshotUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshot(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLabelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Max 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setLabelImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!txHash.trim()) {
      setError(lang === 'de' ? 'Bitte geben Sie den Transaktions-Hash ein.' : 'Please enter the transaction hash.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let screenshotUrl = '';
      if (screenshot) {
        try { screenshotUrl = await uploadPaymentScreenshot(certId, screenshot); } catch (e) { console.warn('Screenshot upload failed:', e); }
      }
      let labelUrl = '';
      if (labelImage) {
        try { labelUrl = await uploadProductLabel(certId, labelImage); } catch (e) { console.warn('Label upload failed:', e); }
      }
      await saveCertificate(user.uid, user.email, {
        certId, contact, sample, analysis, includeQR, includeLogo, lang,
        logoSnapshot: includeLogo ? getUserLogo() : null,
        certType: certType.key,
        certPrice: certType.price,
        paymentCrypto: selectedCrypto.key,
        paymentTxHash: txHash.trim(),
        paymentScreenshot: screenshotUrl,
        productLabelUrl: labelUrl,
      });
      setSubmitted(true);
    } catch (e) {
      console.error('Submit error:', e?.code, e?.message, e);
      setError(`${t('common.error')} (${e?.code || e?.message || 'unknown'})`);
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div style={s.page}>
        <div style={s.successBox}>
          <div style={s.pendingCircle}>⏳</div>
          <h2 style={s.successTitle}>{t('coa.pending.title')}</h2>
          <p style={s.successSub}>{t('coa.pending.subtitle')}</p>
          <div style={s.certIdBox}>
            <span style={s.certIdLabel}>{t('coa.preview.certId')}</span>
            <span style={s.certIdVal}>{certId}</span>
          </div>
          <div style={s.pendingBadge}>⏳ {t('coa.pending.badge')}</div>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap', justifyContent:'center', marginTop:28 }}>
            <button style={s.anotherBtn} onClick={() => window.location.reload()}>{t('coa.success.another')}</button>
            <button style={s.dashBtn}    onClick={() => navigate('/certificates')}>{t('coa.pending.viewStatus')}</button>
          </div>
        </div>
      </div>
    );
  }

  const stepLabels = ['type','contact','sample','analysis','payment','review']
    .map(k => t('wizard.steps.' + k));

  return (
    <div style={s.page}>
      {/* Stepper */}
      <div style={s.stepperWrap}>
        {STEPS.map((st, i) => (
          <React.Fragment key={st}>
            <div style={s.stepItem}>
              <div style={{ ...s.stepCircle, ...(i === step ? s.stepActive : i < step ? s.stepDone : {}) }}>
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{ ...s.stepLabel, ...(i === step ? s.stepLabelActive : {}) }}>
                {stepLabels[i]}
              </span>
            </div>
            {i < STEPS.length - 1 && <div style={{ ...s.stepLine, ...(i < step ? s.stepLineDone : {}) }} />}
          </React.Fragment>
        ))}
      </div>

      <div style={s.card}>
        {step === 0 && <StepType certType={certType} setCertType={setCertType} lang={lang} t={t} onNext={handleNext} />}
        {step === 1 && <StepContact contact={contact} setC={setC} lang={lang} t={t} onBack={handleBack} onNext={handleNext} />}
        {step === 2 && <StepSample sample={sample} setS={setS} toggleMulti={toggleMulti} lang={lang} t={t} labelImage={labelImage} labelRef={labelRef} onLabelUpload={handleLabelUpload} onBack={handleBack} onNext={handleNext} />}
        {step === 3 && <StepAnalysis analysis={analysis} toggleAnalysis={toggleAnalysis} setAnalysisSpec={setAnalysisSpec} lang={lang} t={t} onBack={handleBack} onNext={handleNext} />}
        {step === 4 && (
          <StepPayment
            certType={certType} lang={lang} t={t}
            selectedCrypto={selectedCrypto} setSelectedCrypto={setSelectedCrypto}
            txHash={txHash} setTxHash={setTxHash}
            screenshot={screenshot} screenshotRef={screenshotRef}
            onScreenshotUpload={handleScreenshotUpload}
            onBack={handleBack} onNext={handleNext}
          />
        )}
        {step === 5 && (
          <StepReview
            contact={contact} sample={sample} analysis={analysis}
            certType={certType} lang={lang} certId={certId}
            includeQR={includeQR} includeLogo={includeLogo} setIncludeLogo={setIncludeLogo}
            txHash={txHash} selectedCrypto={selectedCrypto}
            t={t} onBack={handleBack} onSubmit={handleSubmit}
            saving={saving} error={error}
          />
        )}
      </div>
    </div>
  );
}

// ── Step 0: Type ──────────────────────────────────────────────────────────────
function StepType({ certType, setCertType, lang, t, onNext }) {
  return (
    <div>
      <h2 style={s.cardTitle}>{t('wizard.selectType')}</h2>
      <p style={s.cardSub}>{t('wizard.selectTypeSub')}</p>
      <div style={s.typeGrid}>
        {CERT_TYPES.map(ct => (
          <button key={ct.key} style={{ ...s.typeCard, ...(certType.key === ct.key ? s.typeCardActive : {}) }} onClick={() => setCertType(ct)}>
            {ct.popular && <div style={s.popularBadge}>{t('wizard.popular')}</div>}
            <div style={s.typeName}>{ct.name[lang] || ct.name.en}</div>
            <div style={s.typePrice}><span style={s.typePriceNum}>${ct.price}</span><span style={s.typePriceUnit}> USD</span></div>
            <p style={s.typeDesc}>{ct.desc[lang] || ct.desc.en}</p>
            <ul style={s.typeFeatures}>
              {(ct.features[lang] || ct.features.en).map((f,i) => (
                <li key={i} style={s.typeFeature}><span style={s.typeCheck}>✓</span> {f}</li>
              ))}
            </ul>
            {certType.key === ct.key && <div style={s.typeSelected}>✓ {t('wizard.selectedLabel')}</div>}
          </button>
        ))}
      </div>
      <div style={s.actions}><div /><button style={s.nextBtn} onClick={onNext}>{t('wizard.next')}</button></div>
    </div>
  );
}

// ── Step 1: Contact Info ──────────────────────────────────────────────────────
function StepContact({ contact: c, setC, lang, t, onBack, onNext }) {
  const de = lang === 'de';
  return (
    <div>
      <h2 style={s.cardTitle}>{t('wizard.contactTitle')}</h2>
      <p style={s.cardSub}>{t('wizard.contactSub')}</p>
      <div style={s.grid2}>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={s.label}>{t('wizard.companyName')} <span style={s.required}>*</span></label>
          <input style={s.input} value={c.companyName} onChange={setC('companyName')} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <label style={s.label}>{t('wizard.address')}</label>
          <input style={s.input} value={c.address} onChange={setC('address')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.requestDate')}</label>
          <input style={s.input} type="date" value={c.requestDate} onChange={setC('requestDate')} />
        </div>
      </div>
      <div style={s.actions}>
        <button style={s.backBtn} onClick={onBack}>←</button>
        <button style={s.nextBtn} onClick={onNext} disabled={!c.companyName.trim()}>
          {t('wizard.next')}
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Sample Info ──────────────────────────────────────────────────────
// Dropdown options
const LINES           = ['human','veterinary','agricultural'];
const CATEGORIES      = ['medication','cosmetic','supplement','phytotherapy','homeopathic','other'];
const SAMPLE_TYPES    = ['MP','PG','PT','DM','MEE','AP','APT','CEA'];
const METHODOLOGIES   = ['USP','BP','EP','ISO','Client','Others'];
const STORAGE_OPTIONS = ['ambient','refrigeration','light-sensitive'];
const PRESENTATIONS   = ['Tabletas','Cápsulas','Solución','Suspensión','Crema','Gel','Polvo','Ampolla','Jarabe','Óvulos','Supositorios','Gotas','Spray','Parche','Otro'];
const PHARM_FORMS     = ['Sólida','Líquida','Semi-sólida','Gaseosa','Otra'];
const CERT_OPTIONS    = ['SI','NO'];
const TRANSPORT_TEMPS = ['2-8°C','15-25°C','25±2°C','<-15°C','Ambiente'];

function StepSample({ sample: sm, setS, toggleMulti, lang, t, labelImage, labelRef, onLabelUpload, onBack, onNext }) {
  const de = lang === 'de';



  return (
    <div>
      <h2 style={s.cardTitle}>{t('wizard.sampleTitle')}</h2>
      <p style={s.cardSub}>{t('wizard.sampleSub')}</p>

      {/* ── Product identification ── */}
      <div style={s.fieldSection}>{t('wizard.sectionProduct')}</div>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t('wizard.productName')} <span style={s.required}>*</span></label>
          <input style={s.input} value={sm.productName} onChange={setS('productName')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.lot')} <span style={s.required}>*</span></label>
          <input style={s.input} value={sm.lot} onChange={setS('lot')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.presentation')}</label>
          <select style={s.select} value={sm.presentation} onChange={setS('presentation')}>
            <option value="">{t('wizard.select')}</option>
            {t('wizard.presentations').map((v, i) => <option key={i} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={s.label}>{t('wizard.pharmaceuticalForm')}</label>
          <select style={s.select} value={sm.pharmaceuticalForm} onChange={setS('pharmaceuticalForm')}>
            <option value="">{t('wizard.select')}</option>
            {t('wizard.pharmaForms').map((v, i) => <option key={i} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={s.label}>{t('wizard.sampleQty')}</label>
          <input style={s.input} value={sm.sampleQty} onChange={setS('sampleQty')} placeholder={t('wizard.sampleQtyPh')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.activeIngredient')}</label>
          <input style={s.input} value={sm.activeIngredient} onChange={setS('activeIngredient')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.doseConcentration')}</label>
          <input style={s.input} value={sm.doseConcentration} onChange={setS('doseConcentration')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.sanitaryRegistry')}</label>
          <input style={s.input} value={sm.sanitaryRegistry} onChange={setS('sanitaryRegistry')} />
        </div>
      </div>

      {/* ── Dates ── */}
      <div style={s.fieldSection}>{t('wizard.sectionDates')}</div>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t('wizard.mfgDate')}</label>
          <input style={s.input} type="date" value={sm.mfgDate} onChange={setS('mfgDate')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.expDate')}</label>
          <input style={s.input} type="date" value={sm.expDate} onChange={setS('expDate')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.samplingDate')}</label>
          <input style={s.input} type="date" value={sm.samplingDate} onChange={setS('samplingDate')} />
        </div>
      </div>

      {/* ── Origin ── */}
      <div style={s.fieldSection}>{t('wizard.sectionOrigin')}</div>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t('wizard.supplier')}</label>
          <input style={s.input} value={sm.supplier} onChange={setS('supplier')} />
        </div>
        <div>
          <label style={s.label}>{t('wizard.manufacturer')}</label>
          <input style={s.input} value={sm.manufacturer} onChange={setS('manufacturer')} />
        </div>
      </div>

      {/* ── Classification ── */}
      <div style={s.fieldSection}>{t('wizard.sectionClass')}</div>

      <label style={s.label}>{t('wizard.productLine')}</label>
      <select style={{ ...s.select, marginBottom: 14 }} value={sm.line} onChange={setS('line')}>
        <option value="">{t('wizard.select')}</option>
        {t('wizard.lineValues').map((v, i) => <option key={v} value={v}>{t('wizard.lines')[i]}</option>)}
      </select>

      <label style={s.label}>{t('wizard.productCategory')}</label>
      <select style={{ ...s.select, marginBottom: 14 }} value={sm.category} onChange={setS('category')}>
        <option value="">{t('wizard.select')}</option>
        {t('wizard.categoryValues').map((v, i) => <option key={v} value={v}>{t('wizard.categories')[i]}</option>)}
      </select>

      {/* Sample type — multi chips */}
      <label style={s.label}>{t('wizard.sampleTypeLabel')}</label>
      <div style={s.chipRow}>
        {SAMPLE_TYPES.map(val => (
          <button key={val} style={{ ...s.chip, ...(sm.sampleType.includes(val) ? s.chipActive : {}) }} onClick={() => toggleMulti('sampleType', val)}>
            {val}
          </button>
        ))}
      </div>
      <div style={s.chipHint}>
        {t('wizard.sampleTypeHint')}
      </div>

      {/* ── Storage & Transport ── */}
      <div style={s.fieldSection}>{t('wizard.sectionStorage')}</div>
      <div style={s.grid2}>
        <div>
          <label style={s.label}>{t('wizard.storageConditions')}</label>
          <select style={s.select} value={sm.storageCondition} onChange={setS('storageCondition')}>
            <option value="">{t('wizard.select')}</option>
            {t('wizard.storageValues').map((v, i) => <option key={v} value={v}>{t('wizard.storageOpts')[i]}</option>)}
          </select>
        </div>
        <div>
          <label style={s.label}>{t('wizard.transportTemp')}</label>
          <select style={s.select} value={sm.transportTemp} onChange={setS('transportTemp')}>
            <option value="">{t('wizard.select')}</option>
            {t('wizard.transportOpts').map((v, i) => <option key={i} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={s.label}>{t('wizard.manufacturerCert')}</label>
          <select style={s.select} value={sm.hasManufacturerCert} onChange={setS('hasManufacturerCert')}>
            <option value="">{t('wizard.select')}</option>
            {CERT_OPTIONS.map(v => <option key={v} value={v}>{v === 'SI' ? t('wizard.hasManufacturerCertYes') : t('wizard.hasManufacturerCertNo')}</option>)}
          </select>
        </div>
      </div>

      {/* ── Methodology — multi chips ── */}
      <div style={s.fieldSection}>{t('wizard.sectionMethodology')}</div>
      <div style={s.chipRow}>
        {t('wizard.methodologies').map(val => (
          <button key={val} style={{ ...s.chip, ...(sm.methodology.includes(val) ? s.chipActive : {}) }} onClick={() => toggleMulti('methodology', val)}>
            {val}
          </button>
        ))}
      </div>

      {/* ── Label image ── */}
      <div style={s.fieldSection}>{t('wizard.sectionLabel')}</div>
      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginBottom: 12 }}>
        {de
          ? 'Laden Sie ein Foto des Produktetiketts hoch. Es erscheint im PDF und auf der Verifizierungsseite.'
          : 'Upload a photo of the product label. It will appear in the PDF and on the verification page.'}
      </p>
      {labelImage ? (
        <div style={s.labelPreviewWrap}>
          <img src={labelImage} alt="Product label" style={s.labelPreviewImg} />
          <div style={s.labelPreviewOverlay}>
            <button style={s.labelChangeBtn} onClick={() => labelRef.current.click()}>
              📷 {t('wizard.labelChange')}
            </button>
          </div>
          <div style={s.labelPreviewBadge}>✓ {t('wizard.labelUploaded')}</div>
        </div>
      ) : (
        <button style={s.labelUploadBtn} onClick={() => labelRef.current.click()}>
          <span style={s.labelUploadIcon}>🏷️</span>
          <div>
            <div style={s.labelUploadTitle}>{t('wizard.labelUpload')}</div>
            <div style={s.labelUploadHint}>{t('wizard.labelHint')}</div>
          </div>
        </button>
      )}
      <input ref={labelRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={onLabelUpload} />

      <div style={s.actions}>
        <button style={s.backBtn} onClick={onBack}>←</button>
        <button style={s.nextBtn} onClick={onNext} disabled={!sm.productName.trim() || !sm.lot.trim()}>
          {t('wizard.next')}
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Analysis Requested ────────────────────────────────────────────────
function StepAnalysis({ analysis, toggleAnalysis, setAnalysisSpec, lang, t, onBack, onNext }) {
  const de = lang === 'de';

  // Field definitions [key, EN label, DE label]
  const fields = [
    ['aspecto',              'Appearance (Aspecto)',          'Aussehen (Aspecto)'],
    ['ph',                   'pH',                            'pH'],
    ['conductividad',        'Conductivity',                  'Leitfähigkeit'],
    ['toc',                  'TOC',                           'TOC'],
    ['nitritos',             'Nitrites',                      'Nitrite'],
    ['nitratos',             'Nitrates',                      'Nitrate'],
    ['color',                'Color',                         'Farbe'],
    ['turbiedad',            'Turbidity',                     'Trübung'],
    ['cloroLibre',           'Free Residual Chlorine',        'Freies Restchlor'],
    ['durezaTotal',          'Total Hardness',                'Gesamthärte'],
    ['sulfatos',             'Sulfates',                      'Sulfate'],
    ['alcalinidad',          'Alkalinity / Acidity',          'Alkalinität / Acidität'],
    ['metalesPesados',       'Heavy Metals',                  'Schwermetalle'],
    ['densidad',             'Density',                       'Dichte'],
    ['kFisher',              'Karl Fischer (Water content)',  'Karl Fischer (Wassergehalt)'],
    ['solubilidad',          'Solubility',                    'Löslichkeit'],
    ['puntoDeFusion',        'Melting Point',                 'Schmelzpunkt'],
    ['viscosidad',           'Viscosity',                     'Viskosität'],
    ['perdidaPorSecado',     'Loss on Drying',                'Verlust beim Trocknen'],
    ['indiceRefraccion',     'Refractive Index',              'Brechungsindex'],
    ['residuoIgnicion',      'Ignition Residue',              'Glührückstand'],
    ['rotacionOptica',       'Optical Rotation',              'Optische Drehung'],
    ['indiceYodo',           'Iodine Value',                  'Jodzahl'],
    ['indiceSaponificacion', 'Saponification Value',          'Verseifungszahl'],
    ['acidez',               'Acidity',                       'Säuregehalt'],
    ['materiaAnionica',      'Anionic Matter Content',        'Anionische Materie'],
    ['friabilidad',          'Friability',                    'Friabilität'],
    ['dureza',               'Hardness',                      'Härte'],
    ['desintegracion',       'Disintegration',                'Zerfall'],
    ['disolucion',           'Dissolution',                   'Auflösung'],
    ['pesoPromedio',         'Average Weight',                'Durchschnittsgewicht'],
    ['volumetria',           'Volumetry',                     'Volumetrie'],
    ['valoracionUV',         'UV Assay',                      'UV-Valorisierung'],
    ['valoracionHPLC',       'HPLC Assay',                    'HPLC-Valorisierung'],
    ['otros',                'Other analyses',                'Andere Analysen'],
  ];

  const selectedCount = fields.filter(([k]) => analysis[k]?.selected).length;

  return (
    <div>
      <h2 style={s.cardTitle}>{t('wizard.analysisTitle')}</h2>
      <p style={s.cardSub}>
        {t('wizard.analysisSub')}
        {' '}<strong style={{ color: '#4A9EFF' }}>{selectedCount} {t('wizard.selected')}</strong>
      </p>

      <div style={s.analysisGrid}>
        {fields.map(([key, enLabel, deLabel]) => {
          const item = analysis[key] || { selected: false, spec: '' };
          return (
            <div key={key} style={{ ...s.analysisRow, ...(item.selected ? s.analysisRowActive : {}) }}>
              <button style={s.analysisCheck} onClick={() => toggleAnalysis(key)}>
                <span style={{ ...s.checkBox, ...(item.selected ? s.checkBoxActive : {}) }}>
                  {item.selected ? '✓' : ''}
                </span>
                <span style={s.analysisLabel}>{de ? deLabel : enLabel}</span>
              </button>
              {item.selected && (
                <input
                  style={s.specInput}
                  placeholder={t('wizard.specPlaceholder')}
                  value={item.spec}
                  onChange={e => setAnalysisSpec(key, e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 20 }}>
        <label style={s.label}>{t('wizard.obsLabel')}</label>
        <textarea
          style={{ ...s.input, minHeight: 80, resize: 'vertical' }}
          value={analysis.observations}
          onChange={e => setAnalysisSpec('observations', e.target.value)}
          placeholder={t('wizard.obsPh')}
        />
      </div>

      <div style={s.actions}>
        <button style={s.backBtn} onClick={onBack}>←</button>
        <button style={s.nextBtn} onClick={onNext} disabled={selectedCount === 0}>
          {t('wizard.next')}
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Payment ───────────────────────────────────────────────────────────
function StepPayment({ certType, lang, t, selectedCrypto, setSelectedCrypto, txHash, setTxHash, screenshot, screenshotRef, onScreenshotUpload, onBack, onNext }) {
  const [copied, setCopied] = useState(false);
  const de = lang === 'de';

  const copyAddress = () => {
    navigator.clipboard.writeText(selectedCrypto.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div>
      <h2 style={s.cardTitle}>{t('wizard.paymentTitle')}</h2>
      <p style={s.cardSub}>{t('wizard.paymentSub')}</p>

      {/* Amount */}
      <div style={s.amountBanner}>
        <div style={s.amountLabel}>{t('wizard.amountLabel')}</div>
        <div style={s.amountValue}>${certType.price}<span style={s.amountCurrency}> USD</span></div>
        <div style={s.amountType}>{certType.name[lang] || certType.name.en}</div>
      </div>

      {/* Crypto selector */}
      <div style={s.cryptoLabel}>{t('wizard.selectMethod')}</div>
      <div style={s.cryptoSelector}>
        {CRYPTO_WALLETS.map(w => (
          <button key={w.key} style={{ ...s.cryptoBtn, ...(selectedCrypto.key === w.key ? s.cryptoBtnActive : {}), borderColor: selectedCrypto.key === w.key ? w.color : 'rgba(255,255,255,0.1)' }} onClick={() => setSelectedCrypto(w)}>
            <span style={{ ...s.cryptoIcon, color: w.color }}>{w.icon}</span>
            <div><div style={s.cryptoName}>{w.name}</div><div style={s.cryptoNetwork}>{w.network}</div></div>
          </button>
        ))}
      </div>

      {/* Wallet address */}
      <div style={s.walletCard}>
        <div style={s.walletLabel}>{t('wizard.sendToAddress')}</div>
        <div style={s.walletRow}>
          <code style={s.walletAddress}>{selectedCrypto.address}</code>
          <button style={s.copyBtn} onClick={copyAddress}>{copied ? '✓ ' + t('wizard.copied') : t('wizard.copy')}</button>
        </div>
        <div style={s.walletNetwork}><span style={s.walletNetworkDot} />{t('wizard.network')}: <strong>{selectedCrypto.network}</strong></div>
      </div>

      <div style={s.importantBox}>
        <strong>{t('wizard.important')}</strong>
        <p style={{ margin:'4px 0 0', fontSize:13 }}>{t('wizard.importantNote')}</p>
      </div>

      {/* TX Hash */}
      <div style={s.fieldGroup}>
        <label style={s.label}>{t('wizard.txHashLabel')}</label>
        <p style={s.fieldNote}>{t('wizard.txHashNote')}</p>
        <input style={{ ...s.input, fontFamily:'monospace', fontSize:13 }} placeholder={t('wizard.txHashPh')} value={txHash} onChange={e => setTxHash(e.target.value)} />
        {txHash.trim() && (
          <a href={`${selectedCrypto.explorerUrl}${txHash.trim()}`} target="_blank" rel="noopener noreferrer" style={s.explorerLink}>
            🔍 {de ? 'Transaktion prüfen auf' : 'Verify on'} {selectedCrypto.explorerName} ↗
          </a>
        )}
      </div>

      {/* Screenshot */}
      <div style={s.fieldGroup}>
        <label style={s.label}>{t('wizard.screenshotLabel')}</label>
        <p style={s.fieldNote}>{t('wizard.screenshotNote')}</p>
        {screenshot ? (
          <div>
            <img src={screenshot} alt="Payment" style={{ maxWidth:'100%', maxHeight:160, borderRadius:8, border:'1px solid rgba(255,255,255,0.1)' }} />
            <button style={s.changeScreenshotBtn} onClick={() => screenshotRef.current.click()}>{t('wizard.changeScreenshot')}</button>
          </div>
        ) : (
          <button style={s.uploadScreenshotBtn} onClick={() => screenshotRef.current.click()}>{t('wizard.attachScreenshot')}</button>
        )}
        <input ref={screenshotRef} type="file" accept="image/*" style={{ display:'none' }} onChange={onScreenshotUpload} />
      </div>

      <div style={s.actions}>
        <button style={s.backBtn} onClick={onBack}>←</button>
        <button style={s.nextBtn} onClick={onNext} disabled={!txHash.trim()}>{t('wizard.next')}</button>
      </div>
    </div>
  );
}

// ── Step 5: Review & Submit ───────────────────────────────────────────────────
function StepReview({ contact, sample, analysis, certType, lang, certId, includeQR, includeLogo, setIncludeLogo, txHash, selectedCrypto, t, onBack, onSubmit, saving, error }) {
  const de = lang === 'de';
  const selectedAnalyses = Object.entries(analysis).filter(([k,v]) => k !== 'observations' && v?.selected).map(([k,v]) => k);

  return (
    <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
      {/* Summary */}
      <div style={{ flex: '1 1 380px' }}>
        <h2 style={s.cardTitle}>{t('wizard.reviewTitle')}</h2>
        <p style={s.cardSub}>{t('wizard.reviewSub')}</p>

        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>{t('wizard.selectType')}</div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>{t('wizard.summaryType')}</span><span style={s.reviewVal}>{certType.name[lang] || certType.name.en}</span></div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>{t('wizard.summaryPrice')}</span><span style={{ ...s.reviewVal, color:'#FBBF24', fontWeight:800 }}>${certType.price} USD</span></div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>ID</span><span style={{ ...s.reviewVal, fontFamily:'monospace', color:'#4A9EFF' }}>{certId}</span></div>
        </div>

        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>{t('wizard.contactTitle')}</div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>{t('wizard.companyName')}</span><span style={s.reviewVal}>{contact.companyName}</span></div>
          {contact.purchaseOrder && <div style={s.reviewRow}><span style={s.reviewLabel}>PO #</span><span style={s.reviewVal}>{contact.purchaseOrder}</span></div>}
        </div>

        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>{t('wizard.sampleTitle')}</div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>{t('wizard.productName')}</span><span style={s.reviewVal}>{sample.productName}</span></div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>{t('wizard.lot')}</span><span style={s.reviewVal}>{sample.lot}</span></div>
          {sample.sampleQty && <div style={s.reviewRow}><span style={s.reviewLabel}>{de ? 'Menge' : 'Qty'}</span><span style={s.reviewVal}>{sample.sampleQty}</span></div>}
        </div>

        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>{t('wizard.analysisTitle')} ({selectedAnalyses.length})</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
            {selectedAnalyses.map(k => (
              <span key={k} style={s.analysisPill}>{k}</span>
            ))}
          </div>
        </div>

        <div style={s.reviewSection}>
          <div style={s.reviewSectionTitle}>{t('wizard.paymentTitle')}</div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>{t('wizard.summaryPayment')}</span><span style={s.reviewVal}>{selectedCrypto.name}</span></div>
          <div style={s.reviewRow}><span style={s.reviewLabel}>TX Hash</span><code style={{ fontSize:11, color:'#4A9EFF', wordBreak:'break-all' }}>{txHash.slice(0,32)}...</code></div>
        </div>
      </div>

      {/* Options */}
      <div style={{ flex:'0 0 240px' }}>
        <h2 style={s.cardTitle}>{t('wizard.options')}</h2>
        <div style={s.optionRow}>
          <span style={s.optionLabel}>{t('coa.preview.includeLogo')}</span>
          <Toggle checked={includeLogo} onChange={setIncludeLogo} />
        </div>
        <div style={s.optionRow}>
          <span style={s.optionLabel}>{t('coa.preview.includeQR')}</span>
          <span style={s.qrAlwaysOn}>✓ {t('coa.preview.qrRequired')}</span>
        </div>

        <div style={s.approvalNotice}>
          <span>ℹ️</span>
          <span style={s.approvalText}>{t('coa.pending.notice')}</span>
        </div>

        {error && <div style={s.err}>{error}</div>}

        <div style={{ ...s.actions, marginTop: 24, flexDirection: 'column', gap: 10 }}>
          <button style={s.submitBtn} onClick={onSubmit} disabled={saving}>
            {saving ? t('common.loading') : `📤 ${t('coa.pending.submit')}`}
          </button>
          <button style={s.backBtn} onClick={onBack}>{t('wizard.back')}</button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)} style={{ width:44, height:24, borderRadius:12, border:'none', cursor:'pointer', background: checked ? '#1E5EFF' : 'rgba(255,255,255,0.15)', position:'relative', transition:'background 0.2s' }}>
      <span style={{ position:'absolute', top:3, left: checked ? 22 : 3, width:18, height:18, borderRadius:9, background:'#fff', transition:'left 0.2s' }} />
    </button>
  );
}

const s = {
  page:            { minHeight:'100vh', background:'#070F2B', padding:'84px 24px 60px', maxWidth:1100, margin:'0 auto', fontFamily:"'Inter', sans-serif", color:'#fff' },
  stepperWrap:     { display:'flex', alignItems:'center', justifyContent:'center', marginBottom:32, flexWrap:'wrap', gap:2 },
  stepItem:        { display:'flex', flexDirection:'column', alignItems:'center', gap:5 },
  stepCircle:      { width:30, height:30, borderRadius:15, background:'rgba(255,255,255,0.08)', border:'2px solid rgba(255,255,255,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'rgba(255,255,255,0.4)' },
  stepActive:      { background:'#1E5EFF', border:'2px solid #1E5EFF', color:'#fff' },
  stepDone:        { background:'rgba(30,94,255,0.3)', border:'2px solid #1E5EFF', color:'#4A9EFF' },
  stepLabel:       { fontSize:10, color:'rgba(255,255,255,0.35)', textAlign:'center', maxWidth:64 },
  stepLabelActive: { color:'#fff' },
  stepLine:        { flex:1, height:2, background:'rgba(255,255,255,0.08)', minWidth:14, maxWidth:36, marginBottom:18 },
  stepLineDone:    { background:'#1E5EFF' },
  card:            { background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:20, padding:'32px 28px' },
  cardTitle:       { fontSize:22, fontWeight:800, color:'#fff', margin:'0 0 6px' },
  cardSub:         { fontSize:14, color:'rgba(255,255,255,0.5)', marginBottom:24 },
  required:        { color:'#F87171', marginLeft:2 },

  // Type cards
  typeGrid:        { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(195px,1fr))', gap:14, marginBottom:24 },
  typeCard:        { background:'rgba(255,255,255,0.03)', border:'2px solid rgba(255,255,255,0.08)', borderRadius:16, padding:'18px 16px', cursor:'pointer', textAlign:'left', position:'relative', transition:'all 0.2s', fontFamily:'inherit' },
  typeCardActive:  { background:'rgba(30,94,255,0.1)', border:'2px solid #1E5EFF' },
  popularBadge:    { position:'absolute', top:-10, right:12, background:'#1E5EFF', color:'#fff', fontSize:10, fontWeight:700, padding:'3px 10px', borderRadius:20 },
  typeName:        { fontSize:15, fontWeight:800, color:'#fff', marginBottom:8 },
  typePrice:       { marginBottom:10 },
  typePriceNum:    { fontSize:26, fontWeight:900, color:'#4A9EFF' },
  typePriceUnit:   { fontSize:13, color:'rgba(255,255,255,0.5)' },
  typeDesc:        { fontSize:12, color:'rgba(255,255,255,0.5)', lineHeight:1.5, marginBottom:10 },
  typeFeatures:    { listStyle:'none', padding:0, margin:0, display:'flex', flexDirection:'column', gap:4 },
  typeFeature:     { fontSize:11.5, color:'rgba(255,255,255,0.65)', display:'flex', gap:5 },
  typeCheck:       { color:'#34D399', fontWeight:700, flexShrink:0 },
  typeSelected:    { marginTop:10, textAlign:'center', fontSize:12, fontWeight:700, color:'#4A9EFF', background:'rgba(30,94,255,0.1)', padding:'5px', borderRadius:7 },

  // Form fields
  grid2:           { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px,1fr))', gap:16, marginBottom:4 },
  label:           { fontSize:13, fontWeight:600, color:'rgba(255,255,255,0.65)', display:'block', marginBottom:6 },
  fieldNote:       { fontSize:12, color:'rgba(255,255,255,0.35)', marginBottom:8, marginTop:-2 },
  fieldSection:    { fontSize:12, fontWeight:700, color:'#4A9EFF', textTransform:'uppercase', letterSpacing:1, marginTop:24, marginBottom:10, paddingBottom:6, borderBottom:'1px solid rgba(74,158,255,0.15)' },
  input:           { width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:10, padding:'11px 13px', color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },
  select:          { width:'100%', background:'#0A1A4E', border:'1px solid rgba(255,255,255,0.15)', borderRadius:10, padding:'11px 13px', color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box', cursor:'pointer', fontFamily:'inherit' },

  // Chips (multi-select)
  chipRow:         { display:'flex', flexWrap:'wrap', gap:8, marginBottom:4 },
  chip:            { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.65)', padding:'7px 14px', borderRadius:20, cursor:'pointer', fontSize:13, fontWeight:500, fontFamily:'inherit', transition:'all 0.15s' },
  chipActive:      { background:'rgba(30,94,255,0.2)', border:'1px solid #1E5EFF', color:'#fff', fontWeight:700 },
  chipHint:        { fontSize:11, color:'rgba(255,255,255,0.3)', lineHeight:1.6, marginTop:4 },

  // Analysis grid
  analysisGrid:    { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:8 },
  analysisRow:     { background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:10, padding:'10px 12px', transition:'all 0.15s' },
  analysisRowActive:{ background:'rgba(30,94,255,0.08)', border:'1px solid rgba(30,94,255,0.25)' },
  analysisCheck:   { display:'flex', alignItems:'center', gap:10, background:'none', border:'none', cursor:'pointer', width:'100%', fontFamily:'inherit', padding:0 },
  checkBox:        { width:18, height:18, borderRadius:5, border:'2px solid rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:800, color:'#fff', flexShrink:0 },
  checkBoxActive:  { background:'#1E5EFF', border:'2px solid #1E5EFF' },
  analysisLabel:   { fontSize:13, color:'rgba(255,255,255,0.75)', textAlign:'left', lineHeight:1.4 },
  specInput:       { marginTop:8, width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:7, padding:'7px 10px', color:'#fff', fontSize:12, outline:'none', boxSizing:'border-box', fontFamily:'inherit' },

  // Payment
  amountBanner:    { background:'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))', border:'1px solid rgba(245,158,11,0.3)', borderRadius:14, padding:'18px 22px', marginBottom:20, textAlign:'center' },
  amountLabel:     { fontSize:11, fontWeight:600, color:'rgba(245,158,11,0.7)', textTransform:'uppercase', letterSpacing:1, marginBottom:5 },
  amountValue:     { fontSize:40, fontWeight:900, color:'#FBBF24', lineHeight:1 },
  amountCurrency:  { fontSize:18, fontWeight:600, color:'rgba(251,191,36,0.7)' },
  amountType:      { fontSize:12, color:'rgba(255,255,255,0.5)', marginTop:4 },
  cryptoLabel:     { fontSize:13, fontWeight:600, color:'rgba(255,255,255,0.65)', marginBottom:10 },
  cryptoSelector:  { display:'flex', gap:12, marginBottom:18, flexWrap:'wrap' },
  cryptoBtn:       { display:'flex', alignItems:'center', gap:10, background:'rgba(255,255,255,0.04)', border:'2px solid rgba(255,255,255,0.1)', borderRadius:12, padding:'12px 16px', cursor:'pointer', fontFamily:'inherit', transition:'all 0.2s', flex:1, minWidth:150 },
  cryptoBtnActive: { background:'rgba(255,255,255,0.08)' },
  cryptoIcon:      { fontSize:20, fontWeight:900 },
  cryptoName:      { fontSize:14, fontWeight:700, color:'#fff' },
  cryptoNetwork:   { fontSize:11, color:'rgba(255,255,255,0.4)' },
  walletCard:      { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:12, padding:'14px 16px', marginBottom:14 },
  walletLabel:     { fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.45)', marginBottom:8, textTransform:'uppercase', letterSpacing:0.5 },
  walletRow:       { display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' },
  walletAddress:   { fontSize:11, color:'#fff', background:'rgba(255,255,255,0.06)', padding:'7px 10px', borderRadius:7, flex:1, wordBreak:'break-all', fontFamily:'monospace' },
  copyBtn:         { background:'rgba(74,158,255,0.15)', border:'1px solid rgba(74,158,255,0.3)', color:'#4A9EFF', padding:'7px 14px', borderRadius:7, cursor:'pointer', fontSize:13, fontWeight:600, whiteSpace:'nowrap', fontFamily:'inherit' },
  walletNetwork:   { fontSize:12, color:'rgba(255,255,255,0.45)', display:'flex', alignItems:'center', gap:6 },
  walletNetworkDot:{ width:6, height:6, borderRadius:3, background:'#34D399', display:'inline-block' },
  importantBox:    { background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:10, padding:'11px 14px', fontSize:13, color:'#FBBF24', marginBottom:18 },
  fieldGroup:      { marginBottom:18 },
  explorerLink:    { display:'inline-block', marginTop:7, fontSize:12, color:'#4A9EFF', textDecoration:'none', fontWeight:600 },
  changeScreenshotBtn:{ display:'block', marginTop:6, background:'none', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.55)', padding:'5px 12px', borderRadius:7, cursor:'pointer', fontSize:12, fontFamily:'inherit' },
  uploadScreenshotBtn:{ background:'rgba(255,255,255,0.04)', border:'2px dashed rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.45)', padding:'14px 20px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'inherit', width:'100%' },

  // Review step
  reviewSection:    { background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:12, padding:'14px 16px', marginBottom:12 },
  reviewSectionTitle:{ fontSize:11, fontWeight:700, color:'#4A9EFF', textTransform:'uppercase', letterSpacing:0.8, marginBottom:8 },
  reviewRow:        { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', gap:12 },
  reviewLabel:      { fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 },
  reviewVal:        { fontSize:13, color:'#fff', fontWeight:500, textAlign:'right' },
  analysisPill:     { fontSize:11, fontWeight:600, color:'#4A9EFF', background:'rgba(74,158,255,0.1)', padding:'3px 9px', borderRadius:12 },

  // Options panel
  optionRow:        { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.06)' },
  optionLabel:      { fontSize:14, color:'rgba(255,255,255,0.7)' },
  qrAlwaysOn:       { fontSize:12, fontWeight:700, color:'#34D399', background:'rgba(52,211,153,0.12)', padding:'3px 10px', borderRadius:20 },
  approvalNotice:   { display:'flex', gap:10, alignItems:'flex-start', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:10, padding:'12px 14px', marginTop:20 },
  approvalText:     { fontSize:12, color:'rgba(251,191,36,0.9)', lineHeight:1.5 },
  err:              { background:'rgba(239,68,68,0.1)', borderRadius:10, padding:'10px', color:'#FCA5A5', fontSize:13, marginTop:12 },

  // Buttons
  actions:          { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:28 },
  backBtn:          { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', color:'#fff', padding:'11px 22px', borderRadius:10, fontSize:14, cursor:'pointer', fontWeight:600 },
  nextBtn:          { background:'linear-gradient(135deg, #1E5EFF, #0A3DD4)', color:'#fff', border:'none', padding:'11px 26px', borderRadius:10, fontSize:14, cursor:'pointer', fontWeight:700 },
  submitBtn:        { background:'linear-gradient(135deg, #F59E0B, #D97706)', color:'#fff', border:'none', padding:'13px', borderRadius:10, fontSize:15, cursor:'pointer', fontWeight:700, width:'100%' },

  // Success
  successBox:       { minHeight:'80vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:24 },
  pendingCircle:    { width:80, height:80, borderRadius:40, background:'rgba(245,158,11,0.15)', border:'2px solid #F59E0B', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, marginBottom:24 },
  successTitle:     { fontSize:24, fontWeight:800, color:'#fff', maxWidth:480, margin:'0 0 10px' },
  successSub:       { fontSize:15, color:'rgba(255,255,255,0.6)', marginBottom:20, maxWidth:400 },
  certIdBox:        { background:'rgba(74,158,255,0.1)', border:'1px solid rgba(74,158,255,0.2)', borderRadius:12, padding:'14px 28px', display:'flex', flexDirection:'column', gap:4 },
  certIdLabel:      { fontSize:12, color:'rgba(255,255,255,0.5)' },
  certIdVal:        { fontFamily:'monospace', fontSize:20, fontWeight:800, color:'#4A9EFF' },
  pendingBadge:     { marginTop:16, background:'rgba(245,158,11,0.15)', border:'1px solid rgba(245,158,11,0.3)', color:'#FBBF24', padding:'8px 18px', borderRadius:20, fontSize:13, fontWeight:700 },
  anotherBtn:       { background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.15)', color:'#fff', padding:'13px 24px', borderRadius:12, fontSize:15, cursor:'pointer', fontWeight:600 },
  dashBtn:          { background:'linear-gradient(135deg, #1E5EFF, #0A3DD4)', border:'none', color:'#fff', padding:'13px 24px', borderRadius:12, fontSize:15, cursor:'pointer', fontWeight:700 },

  // Label upload
  labelUploadBtn:    { display:'flex', alignItems:'center', gap:16, background:'rgba(255,255,255,0.03)', border:'2px dashed rgba(255,255,255,0.12)', borderRadius:14, padding:'20px 24px', cursor:'pointer', width:'100%', fontFamily:'inherit', transition:'all 0.2s' },
  labelUploadIcon:   { fontSize:32, flexShrink:0 },
  labelUploadTitle:  { fontSize:15, fontWeight:600, color:'rgba(255,255,255,0.8)', marginBottom:4, textAlign:'left' },
  labelUploadHint:   { fontSize:12, color:'rgba(255,255,255,0.35)', textAlign:'left' },
  labelPreviewWrap:  { position:'relative', display:'inline-block', maxWidth:'100%' },
  labelPreviewImg:   { maxWidth:'100%', maxHeight:240, borderRadius:12, border:'1px solid rgba(255,255,255,0.1)', display:'block' },
  labelPreviewOverlay:{ position:'absolute', top:8, right:8, display:'flex', gap:8 },
  labelChangeBtn:    { background:'rgba(0,0,0,0.6)', border:'1px solid rgba(255,255,255,0.2)', color:'#fff', padding:'6px 12px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit' },
  labelRemoveBtn:    { background:'rgba(239,68,68,0.7)', border:'none', color:'#fff', width:30, height:30, borderRadius:8, cursor:'pointer', fontSize:14, fontFamily:'inherit' },
  labelPreviewBadge: { marginTop:8, fontSize:12, fontWeight:700, color:'#34D399', display:'flex', alignItems:'center', gap:4 },
};
